import { useMemo, useRef, useState } from "react";
import { useDismissOnOutsideClick } from "../../hooks/useDismissOnOutsideClick";
import { FieldValue, fieldControlClass, fieldDescribedBy } from "./FieldValue";
import {
  MultiSelectFieldProps,
  MultiSelectOption,
} from "./MultiSelectField.types";

function getSummary(
  selected: MultiSelectOption[],
  placeholder: string
) {
  if (selected.length === 0) {
    return placeholder;
  }
  if (selected.length === 1) {
    return selected[0].label;
  }
  return `${selected.length} selected`;
}

/**
 * Multi-select field: a summary button opening a filterable checkbox list.
 * Option sets here can run to dozens of entries, so the filter isn't optional.
 */
export function MultiSelectField({
  label,
  name,
  value,
  options,
  onChange,
  disabled,
  placeholder = "Please select",
  error,
  className,
  helpText,
}: MultiSelectFieldProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useDismissOnOutsideClick(containerRef, open, () => setOpen(false));

  // Compared as strings, so a numeric option value matches one that arrived as text.
  const selectedKeys = useMemo(
    () => new Set(value.map((selected) => String(selected))),
    [value]
  );

  const selected = options.filter((option) =>
    selectedKeys.has(String(option.value))
  );

  const visibleOptions = useMemo(() => {
    const search = filter.trim().toLowerCase();
    return search
      ? options.filter((option) =>
          option.label.toLowerCase().includes(search)
        )
      : options;
  }, [options, filter]);

  const toggle = (option: MultiSelectOption) => {
    const key = String(option.value);
    onChange(
      selectedKeys.has(key)
        ? value.filter(
            (selectedValue) => String(selectedValue) !== key
          )
        : [...value, option.value]
    );
  };

  return (
    <FieldValue label={label} htmlFor={name} helpText={helpText} error={error} className={className}>
      <div className="position-relative" ref={containerRef}>
        <button
          type="button"
          id={name}
          aria-describedby={fieldDescribedBy(name, helpText, error)}
          disabled={disabled}
          className={fieldControlClass("form-select text-start", error)}
          onClick={() => setOpen((previous) => !previous)}
        >
          <span
            className={
              selected.length === 0 ? "text-muted" : undefined
            }
          >
            {getSummary(selected, placeholder)}
          </span>
        </button>
        {open && (
          <div className="multi-select-field__menu">
            <input
              type="search"
              className="form-control form-control-sm mb-2"
              placeholder="Search"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
            <div className="multi-select-field__options">
              {visibleOptions.length === 0 && (
                <p className="form-text mb-0">No matches</p>
              )}
              {visibleOptions.map((option) => {
                const optionId = `${name}-${option.value}`;
                return (
                  <div className="form-check" key={optionId}>
                    <input
                      className="form-check-input cursor-pointer"
                      type="checkbox"
                      id={optionId}
                      checked={selectedKeys.has(String(option.value))}
                      onChange={() => toggle(option)}
                    />
                    <label
                      className="form-check-label cursor-pointer"
                      htmlFor={optionId}
                    >
                      {option.label}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </FieldValue>
  );
}

export default MultiSelectField;
